import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';
import CurrencyToggle from '../../../components/feature/CurrencyToggle';
import BillingTermToggle from '../../../components/feature/BillingTermToggle';
import {
  CURRENCY_CODE,
  formatPrice,
  termImplementation,
  termMonthlyEquivalent,
  termSavings,
  termTotal,
  useCurrency,
  type BillingTerm,
  type Currency,
} from '../../../hooks/useCurrency';
import PlanFormModal from './PlanFormModal';

const WHATSAPP_BASE =
  'https://wa.me/18495388899?text=Hola%20InnoFlex!%20Soy%20dealer%20y%20me%20interesa%20el%20';

/** Implementación del sistema de facturación — igual para los cuatro planes. */
const DEALER_IMPL: Record<Currency, number> = { USD: 200, DOP: 11800 };

/* Add-ons fiscales: no dependen del término, solo de la moneda. */
const ADDON_NOTE_KEYS: Record<Currency, { fel: string; cert: string }> = {
  USD: { fel: 'dealers_fel_addon', cert: 'dealers_cert' },
  DOP: { fel: 'dealers_fel_addon_dop', cert: 'dealers_cert_dop' },
};

/** Textos que dependen del término elegido. */
const TERM_TEXT: Record<
  BillingTerm,
  { unitKey: string; nounKey: string; billedKey: string | null }
> = {
  monthly: { unitKey: 'pricing_per_month', nounKey: 'pricing_month', billedKey: null },
  semiannual: {
    unitKey: 'pricing_per_semester',
    nounKey: 'pricing_semester',
    billedKey: 'pricing_billed_semiannual',
  },
  annual: {
    unitKey: 'pricing_per_year',
    nounKey: 'pricing_year',
    billedKey: 'pricing_billed_annual',
  },
};

// Live system demo — dealers only. Change these in one place if the demo account rotates.
const DEMO_URL = 'https://app.innoflextechnology.com/auth/login';
const DEMO_USER = 'Dealer';
const DEMO_PASS = 'admin01';

interface DealerPlan {
  id: string;
  name: string;
  taglineKey: string;
  /** Mensualidad por moneda — valor comercial fijo, sin conversión por tasa. */
  monthly: Record<Currency, number>;
  badge: string | null;
  accent: string;
  accentDark: string;
  priceOnRequest?: boolean;
  limits: { users: string; cajas: string; branches: string };
  support: { r: string; c: string; i: string };
  features: string[];
  waText: string;
}

const PLANS: DealerPlan[] = [
  {
    id: 'starter', name: 'Starter', taglineKey: 'dplan_starter_tag', monthly: { USD: 19.99, DOP: 1150 }, badge: null,
    accent: '#06b6d4', accentDark: '#0e7490', waText: 'Plan%20Starter%20Dealers',
    limits: { users: '1', cajas: '1', branches: '1' },
    support: { r: 'support_starter_response', c: 'support_starter_channels', i: 'support_starter_includes' },
    features: ['df_billing_pos', 'df_vehicle_inv_basic', 'df_clients_buyers', 'df_offline', 'df_reports_basic'],
  },
  {
    id: 'business', name: 'Business', taglineKey: 'dplan_business_tag', monthly: { USD: 39.99, DOP: 2350 }, badge: 'pricing_badge_popular',
    accent: '#0ea5e9', accentDark: '#0369a1', waText: 'Plan%20Business%20Dealers',
    limits: { users: '2', cajas: '2', branches: '1' },
    support: { r: 'support_business_response', c: 'support_business_channels', i: 'support_business_includes' },
    features: ['df_includes_starter', 'df_purchases', 'df_receivables', 'df_stock_alerts'],
  },
  {
    id: 'pro', name: 'Pro', taglineKey: 'dplan_pro_tag', monthly: { USD: 59.99, DOP: 3500 }, badge: null,
    accent: '#6366f1', accentDark: '#4338ca', waText: 'Plan%20Pro%20Dealers',
    limits: { users: '4', cajas: '3', branches: '1' },
    support: { r: 'support_pro_response', c: 'support_pro_channels', i: 'support_pro_includes' },
    features: ['df_includes_business', 'df_financing', 'df_smart_alerts', 'df_priority_support'],
  },
  {
    id: 'premium', name: 'Premium', taglineKey: 'dplan_premium_tag', monthly: { USD: 89.99, DOP: 5300 }, badge: 'pricing_badge_complete',
    accent: '#a855f7', accentDark: '#7c3aed', waText: 'Plan%20Premium%20Dealers',
    limits: { users: '6', cajas: '4', branches: '1' },
    support: { r: 'support_premium_response', c: 'support_premium_channels', i: 'support_premium_includes' },
    features: ['df_includes_pro', 'df_imports', 'df_accounting_adv', 'df_reports_adv', 'df_automations', 'df_rentcar', 'df_clients_dashboard', 'df_purchase_orders', 'df_vehicle_costs', 'df_whatsapp_ai'],
  },
];

type Cell = 'check' | 'addon' | 'dash';
const CMP_ROWS: { labelKey: string; cells: Record<string, Cell> }[] = [
  { labelKey: 'dcmp_vehicle_inv',      cells: { starter: 'check', business: 'check', pro: 'check', premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_offline',          cells: { starter: 'check', business: 'check', pro: 'check', premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_receivables',      cells: { starter: 'dash',  business: 'check', pro: 'check', premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_imports',          cells: { starter: 'dash',  business: 'dash',  pro: 'dash',  premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_accounting',       cells: { starter: 'dash',  business: 'dash',  pro: 'dash',  premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_financing',        cells: { starter: 'dash',  business: 'dash',  pro: 'check', premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_support_priority', cells: { starter: 'dash',  business: 'dash',  pro: 'check', premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_reports_adv',      cells: { starter: 'dash',  business: 'dash',  pro: 'dash',  premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_automations',      cells: { starter: 'dash',  business: 'dash',  pro: 'dash',  premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_rentcar',          cells: { starter: 'dash',  business: 'dash',  pro: 'dash',  premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_clients_dashboard',cells: { starter: 'dash',  business: 'dash',  pro: 'dash',  premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_purchase_orders',  cells: { starter: 'dash',  business: 'dash',  pro: 'dash',  premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_vehicle_costs',    cells: { starter: 'dash',  business: 'dash',  pro: 'dash',  premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_whatsapp_ai',      cells: { starter: 'dash',  business: 'dash',  pro: 'dash',  premium: 'check', custom: 'check' } },
  { labelKey: 'dcmp_api',              cells: { starter: 'addon', business: 'addon', pro: 'addon', premium: 'addon', custom: 'check' } },
];

const LIMIT_META: { icon: string; labelKey: string; field: keyof DealerPlan['limits'] }[] = [
  { icon: 'ri-group-line', labelKey: 'compare_lim_users', field: 'users' },
  { icon: 'ri-computer-line', labelKey: 'compare_lim_cajas', field: 'cajas' },
  { icon: 'ri-store-2-line', labelKey: 'compare_lim_branches', field: 'branches' },
];

export default function DealersPricing() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { currency, setCurrency } = useCurrency();
  const isLight = theme === 'light';
  const [term, setTerm] = useState<BillingTerm>('monthly');
  const [showCompare, setShowCompare] = useState(false);
  const [modalPlan, setModalPlan] = useState<{ name: string; color: string } | null>(null);

  const text = TERM_TEXT[term];
  const unitLabel = CURRENCY_CODE[currency] + t(text.unitKey);
  const addons = ADDON_NOTE_KEYS[currency];

  // Implementación: al pagar anual va al 50%.
  const implFull = DEALER_IMPL[currency];
  const implNow = termImplementation(implFull, term);
  const implDiscounted = implNow < implFull;
  const implLabel =
    `${t('dealers_impl_label')} ${formatPrice(implNow, currency)} · ${t('dealers_impl_note')}` +
    (implDiscounted ? ` · ${t('pricing_impl_annual_off')}` : '');

  const termLabels: Record<BillingTerm, string> = {
    monthly: t('pricing_monthly'),
    semiannual: t('pricing_semiannual'),
    annual: t('pricing_annual'),
  };
  const termBadges = {
    semiannual: t('pricing_term_badge_semiannual'),
    annual: t('pricing_term_badge_annual'),
  };

  const handleSelect = (plan: DealerPlan) => {
    if (plan.priceOnRequest) {
      window.open(`${WHATSAPP_BASE}${plan.waText}`, '_blank', 'noopener,noreferrer');
      return;
    }
    setModalPlan({ name: `${plan.name} · Dealers`, color: plan.accent });
  };

  return (
    <section id="dealers" className="bg-navy-950 py-14 md:py-24 px-5 md:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <span className="inline-flex items-center gap-1.5 text-electric text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2 md:mb-3">
            <i className="ri-car-line text-sm"></i>
            {t('dealers_label')}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-4">
            {t('dealers_title_1')} <span className="text-electric">{t('dealers_title_accent')}</span>
          </h2>
          <p className="font-body text-white/60 text-sm md:text-base max-w-xl mx-auto">
            {t('dealers_sub')}
          </p>
        </div>

        {/* Live system demo — dealers only */}
        <div className="mb-9 md:mb-12">
          <div
            className="relative rounded-2xl border overflow-hidden p-6 md:p-8 flex flex-col lg:flex-row lg:items-center gap-6"
            style={{
              borderColor: isLight ? 'rgba(0,207,255,0.35)' : 'rgba(0,207,255,0.28)',
              background: isLight
                ? 'linear-gradient(135deg, rgba(0,207,255,0.10) 0%, #ffffff 65%)'
                : 'linear-gradient(135deg, rgba(0,207,255,0.12) 0%, rgba(8,16,38,0.95) 60%)',
            }}
          >
            <div
              className="absolute -top-16 -right-10 w-64 h-64 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(0,207,255,0.18) 0%, transparent 70%)', filter: 'blur(30px)' }}
            />

            {/* Text */}
            <div className="flex-1 relative z-10">
              <span className="inline-flex items-center gap-1.5 text-electric text-xs font-semibold tracking-widest uppercase font-body mb-2">
                <i className="ri-play-circle-line text-sm"></i>
                {t('dealers_demo_label')}
              </span>
              <h3 className="font-heading text-xl md:text-2xl font-bold mb-2" style={{ color: isLight ? '#0a1628' : '#ffffff' }}>
                {t('dealers_demo_title')}
              </h3>
              <p className="font-body text-sm max-w-lg" style={{ color: isLight ? 'rgba(10,22,40,0.60)' : 'rgba(255,255,255,0.55)' }}>
                {t('dealers_demo_desc')}
              </p>
            </div>

            {/* Credentials + CTA */}
            <div className="relative z-10 flex flex-col gap-3 w-full lg:w-auto lg:min-w-[300px]">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: t('dealers_demo_user_label'), value: DEMO_USER, icon: 'ri-user-line' },
                  { label: t('dealers_demo_pass_label'), value: DEMO_PASS, icon: 'ri-lock-2-line' },
                ].map((c) => (
                  <div
                    key={c.label}
                    className="rounded-xl border px-3 py-2"
                    style={{
                      borderColor: isLight ? 'rgba(0,207,255,0.25)' : 'rgba(0,207,255,0.20)',
                      backgroundColor: isLight ? 'rgba(0,207,255,0.06)' : 'rgba(0,207,255,0.08)',
                    }}
                  >
                    <span className="flex items-center gap-1 font-body text-[10px] uppercase tracking-wide" style={{ color: isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.45)' }}>
                      <i className={`${c.icon} text-[11px]`}></i>{c.label}
                    </span>
                    <span className="font-heading text-sm font-bold select-all" style={{ color: isLight ? '#0a1628' : '#ffffff' }}>{c.value}</span>
                  </div>
                ))}
              </div>
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 font-bold text-sm py-3 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap font-body hover:scale-[1.02] active:scale-[0.97]"
                style={{ background: 'linear-gradient(135deg, #00cfff, #0284c7)', color: '#0a1628', boxShadow: '0 8px 24px rgba(0,207,255,0.35)' }}
              >
                <i className="ri-external-link-line text-base"></i>
                {t('dealers_demo_cta')}
              </a>
            </div>
          </div>
        </div>

        {/* Currency toggle — USD / RD$ */}
        <div className="flex flex-col items-center gap-2 mb-4">
          <CurrencyToggle currency={currency} onChange={setCurrency} />
          <span className="font-body text-[11px] text-white/40">{t('pricing_currency_note')}</span>
        </div>

        {/* Billing term toggle — mensual / semestral / anual */}
        <div className="flex flex-col items-center gap-2 mb-4">
          <BillingTermToggle
            term={term}
            onChange={setTerm}
            labels={termLabels}
            badges={termBadges}
          />
          <span className="font-body text-[11px] text-white/40">{t('pricing_term_note')}</span>
        </div>

        {/* Cost note (non-intrusive, shared) */}
        <div className="flex justify-center mb-9 md:mb-12">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-center max-w-2xl">
            {[
              { icon: 'ri-settings-3-line', text: implLabel },
              { icon: 'ri-bill-line', text: t(addons.fel) },
              { icon: 'ri-shield-check-line', text: t(addons.cert) },
            ].map((n) => (
              <span key={n.text} className="inline-flex items-center gap-1.5 font-body text-[11px] text-white/45">
                <i className={`${n.icon} text-xs text-electric/70`}></i>
                {n.text}
              </span>
            ))}
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5 items-start">
          {PLANS.map((plan) => (
            <DealerCard
              key={plan.id}
              plan={plan}
              currency={currency}
              term={term}
              implLabel={implLabel}
              implDiscounted={implDiscounted}
              unitLabel={unitLabel}
              termNoun={t(text.nounKey)}
              billedLabel={text.billedKey ? t(text.billedKey) : ''}
              isLight={isLight}
              t={t}
              onSelect={handleSelect}
            />
          ))}
        </div>

        {/* Compare toggle */}
        <div className="flex justify-center mt-9 md:mt-12">
          <button
            onClick={() => setShowCompare((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-body text-sm font-semibold cursor-pointer transition-all duration-200 hover:scale-[1.02]"
            style={{
              borderColor: isLight ? 'rgba(0,207,255,0.35)' : 'rgba(0,207,255,0.30)',
              backgroundColor: isLight ? 'rgba(0,207,255,0.08)' : 'rgba(0,207,255,0.10)',
              color: '#00cfff',
            }}
          >
            <i className={`ri-table-line text-base`}></i>
            {showCompare ? t('dealers_compare_hide') : t('dealers_compare_show')}
            <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${showCompare ? 'rotate-180' : ''}`}></i>
          </button>
        </div>

        {showCompare && <DealerCompareTable currency={currency} isLight={isLight} t={t} />}
      </div>

      {modalPlan && (
        <PlanFormModal planName={modalPlan.name} planColor={modalPlan.color} onClose={() => setModalPlan(null)} />
      )}
    </section>
  );
}

// ─── Plan card ────────────────────────────────────────────────────────────────
interface DealerCardProps {
  plan: DealerPlan;
  currency: Currency;
  term: BillingTerm;
  /** Nota de implementación, ya con moneda y descuento aplicados. */
  implLabel: string;
  implDiscounted: boolean;
  /** Sufijo del precio: "USD/mes", "DOP/año"… */
  unitLabel: string;
  /** Nombre del periodo para el ahorro: "mes", "semestre", "año". */
  termNoun: string;
  /** "facturado anual · ahorras" — vacío en el término mensual. */
  billedLabel: string;
  isLight: boolean;
  t: (k: string) => string;
  onSelect: (p: DealerPlan) => void;
}

function DealerCard({ plan, currency, term, implLabel, implDiscounted, unitLabel, termNoun, billedLabel, isLight, t, onSelect }: DealerCardProps) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const accent = plan.accent;
  const recommended = plan.badge === 'pricing_badge_complete';

  const base = plan.monthly[currency];
  const price = formatPrice(termTotal(base, term), currency);
  const savings = termSavings(base, term);

  const cardBg = isLight
    ? recommended ? `linear-gradient(165deg, ${accent}14 0%, #ffffff 55%)` : '#ffffff'
    : recommended ? `linear-gradient(165deg, ${accent}22 0%, rgba(8,16,38,0.98) 60%)` : 'rgba(12,22,48,0.92)';
  const borderColor = recommended
    ? accent + (isLight ? 'aa' : '80')
    : hovered ? accent + (isLight ? '88' : '55') : isLight ? accent + '33' : 'rgba(255,255,255,0.08)';
  const textMuted = isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.50)';

  return (
    <div
      className="relative flex flex-col rounded-2xl border overflow-hidden"
      style={{
        background: cardBg, borderColor,
        boxShadow: isLight
          ? (hovered || recommended ? `0 10px 30px ${accent}22` : '0 2px 12px rgba(0,0,0,0.06)')
          : (recommended ? `0 0 30px ${accent}33, 0 18px 44px rgba(0,0,0,0.45)` : hovered ? `0 0 24px ${accent}22` : 'none'),
        transform: recommended ? 'translateY(-6px)' : hovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(to right, ${accent}, ${accent}55, transparent)`, opacity: recommended || hovered ? 1 : 0.5 }} />

      <div className="flex flex-col flex-1 p-5">
        {/* Badge */}
        {plan.badge ? (
          <div className="self-start inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full font-body mb-3 border"
            style={{ backgroundColor: accent + '1f', borderColor: accent + '4d', color: accent }}>
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: accent }} />
            {t(plan.badge)}
          </div>
        ) : (
          <div className="h-7 mb-3" />
        )}

        {/* Name */}
        <h3 className="font-heading text-xl font-extrabold leading-none" style={{ color: accent }}>{plan.name}</h3>

        {/* Tagline */}
        <p className="font-body text-xs mt-2 mb-4 leading-snug min-h-[48px]" style={{ color: textMuted }}>
          {t(plan.taglineKey)}
        </p>

        {/* Price */}
        {!plan.priceOnRequest ? (
          <>
            <div className="flex items-end gap-1">
              <span className="font-heading text-3xl font-bold leading-none" style={{ color: isLight ? '#0a1628' : '#ffffff' }}>{price}</span>
              <span className="font-body text-xs mb-1" style={{ color: textMuted }}>{unitLabel}</span>
            </div>
            <div className="h-9 mt-1 flex flex-col gap-0.5">
              {savings > 0 && (
                <>
                  <span className="inline-flex self-start items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full font-body" style={{ backgroundColor: accent + '18', color: accent }}>
                    <i className="ri-price-tag-3-line text-[10px]"></i>
                    {billedLabel} {formatPrice(savings, currency)}/{termNoun}
                  </span>
                  <span className="font-body text-[10px]" style={{ color: textMuted }}>
                    {t('pricing_equivalent')} {formatPrice(termMonthlyEquivalent(base, term), currency)}
                    {t('pricing_per_month')}
                  </span>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-end gap-1 min-h-[44px]">
            <span className="font-heading text-2xl font-bold leading-none" style={{ color: accent }}>{t('plan_custom_price')}</span>
          </div>
        )}

        {/* Implementation */}
        <div className="flex items-start gap-1.5 mt-2 mb-4">
          <i className="ri-add-circle-line text-xs mt-0.5 flex-shrink-0" style={{ color: accent }}></i>
          <span className="font-body text-[11px]" style={{ color: implDiscounted ? accent : textMuted }}>
            {implLabel}
          </span>
        </div>

        {/* Limits */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {LIMIT_META.map((l) => (
            <div key={l.field} className="flex flex-col items-center text-center gap-1 rounded-lg px-1.5 py-2"
              style={{ backgroundColor: accent + '10', border: `1px solid ${accent}22` }}>
              <i className={`${l.icon} text-sm`} style={{ color: accent }}></i>
              <p className="font-heading text-xs font-bold leading-none" style={{ color: isLight ? '#0a1628' : '#ffffff' }}>{plan.limits[l.field]}</p>
              <p className="font-body text-[9px] leading-tight" style={{ color: isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.45)' }}>{t(l.labelKey)}</p>
            </div>
          ))}
        </div>

        {/* Collapsible "what's included" */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 mb-3 font-body text-xs font-semibold cursor-pointer transition-colors"
          style={{ backgroundColor: accent + '12', color: accent, border: `1px solid ${accent}2a` }}
        >
          <span className="inline-flex items-center gap-1.5"><i className="ri-list-check-2 text-sm"></i>{open ? t('dealers_hide_included') : t('dealers_whats_included')}</span>
          <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${open ? 'rotate-180' : ''}`}></i>
        </button>

        <div style={{ maxHeight: open ? 480 : 0, opacity: open ? 1 : 0, overflow: 'hidden', transition: 'max-height 0.35s ease, opacity 0.3s ease' }}>
          <ul className="space-y-2 mb-3">
            {plan.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-xs font-body" style={{ color: isLight ? 'rgba(10,22,40,0.80)' : 'rgba(255,255,255,0.80)' }}>
                <i className="ri-checkbox-circle-fill flex-shrink-0 mt-[1px]" style={{ color: accent, fontSize: 14, lineHeight: 1 }} />
                <span style={{ lineHeight: 1.45 }}>{t(f)}</span>
              </li>
            ))}
            {/* Support summary */}
            <li className="flex items-start gap-2 text-xs font-body pt-1 mt-1 border-t" style={{ borderColor: isLight ? 'rgba(10,22,40,0.08)' : 'rgba(255,255,255,0.08)', color: textMuted }}>
              <i className="ri-customer-service-2-line flex-shrink-0 mt-[1px]" style={{ color: accent, fontSize: 14 }} />
              <span style={{ lineHeight: 1.45 }}>{t(plan.support.r)} · {t(plan.support.c)}</span>
            </li>
          </ul>
        </div>

        {/* Spacer to align CTA */}
        <div className="flex-1" />

        {/* CTA */}
        <button
          onClick={() => onSelect(plan)}
          className="w-full flex items-center justify-center gap-2 font-bold text-sm py-3 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap font-body hover:scale-[1.03] active:scale-[0.97] mt-2"
          style={{ background: `linear-gradient(135deg, ${accent}, ${plan.accentDark})`, color: '#ffffff', boxShadow: `0 6px 18px ${accent}30` }}
        >
          {plan.priceOnRequest ? t('dealers_cta_custom') : t('dealers_cta')}
          <i className="ri-arrow-right-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

// ─── Comparison table ─────────────────────────────────────────────────────────
function DealerCompareTable({ currency, isLight, t }: { currency: Currency; isLight: boolean; t: (k: string) => string }) {
  const tableBorder = isLight ? 'rgba(10,22,40,0.12)' : 'rgba(255,255,255,0.08)';
  const rowBorder = isLight ? 'rgba(10,22,40,0.07)' : 'rgba(255,255,255,0.06)';
  const headerBg = isLight ? '#eef3fb' : 'rgba(12,22,48,0.95)';
  const labelBg = isLight ? '#f5f8ff' : 'rgba(12,22,48,0.45)';
  const labelColor = isLight ? 'rgba(10,22,40,0.70)' : 'rgba(255,255,255,0.62)';
  const valueColor = isLight ? '#0a1628' : '#ffffff';
  const mutedColor = isLight ? 'rgba(10,22,40,0.28)' : 'rgba(255,255,255,0.28)';

  const renderCell = (cell: Cell, accent: string) => {
    if (cell === 'check')
      return (
        <div className="flex justify-center">
          <div className="w-5 h-5 flex items-center justify-center rounded-full" style={{ backgroundColor: accent + '22', color: accent }}>
            <i className="ri-check-line text-xs font-bold"></i>
          </div>
        </div>
      );
    if (cell === 'addon')
      return (
        <span className="inline-block font-body text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ backgroundColor: isLight ? 'rgba(208,160,24,0.14)' : 'rgba(250,204,21,0.14)', color: isLight ? '#a06b00' : '#facc15', border: isLight ? '1px solid rgba(208,160,24,0.30)' : '1px solid rgba(250,204,21,0.30)' }}>
          {t('compare_addon_label')}
        </span>
      );
    return <span className="font-body text-sm" style={{ color: mutedColor }}>—</span>;
  };

  return (
    <div className="mt-6 animate-[fadeIn_0.4s_ease]">
      <h3 className="font-heading text-lg md:text-xl font-bold text-white text-center mb-5">{t('dcmp_title')}</h3>
      <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: tableBorder }}>
        <table className="w-full min-w-[820px]" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th className="text-left p-3.5 pl-5 font-body text-xs font-semibold uppercase tracking-wider w-48" style={{ backgroundColor: headerBg, color: labelColor }}>
                {t('dcmp_feature')}
              </th>
              {PLANS.map((plan) => {
                const highlight = plan.badge === 'pricing_badge_complete' || plan.priceOnRequest;
                return (
                  <th key={plan.id} className="p-3.5 text-center" style={{ backgroundColor: highlight ? plan.accent + (isLight ? '14' : '12') : headerBg, borderLeft: `1px solid ${rowBorder}`, minWidth: 108 }}>
                    <div className="font-heading text-sm font-extrabold" style={{ color: plan.accent }}>{plan.name}</div>
                    <div className="font-heading text-[11px] font-bold mt-0.5" style={{ color: plan.priceOnRequest ? plan.accent : valueColor }}>
                      {plan.priceOnRequest ? t('plan_custom_price') : formatPrice(plan.monthly[currency], currency)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {LIMIT_META.map((row, i) => (
              <tr key={row.field} style={{ borderTop: `1px solid ${rowBorder}` }}>
                <td className="p-3 pl-5 font-body text-xs font-semibold" style={{ backgroundColor: labelBg, color: labelColor }}>{t(row.labelKey)}</td>
                {PLANS.map((plan) => (
                  <td key={plan.id} className="p-3 text-center" style={{ borderLeft: `1px solid ${rowBorder}`, backgroundColor: i % 2 === 0 ? (isLight ? 'rgba(10,22,40,0.01)' : 'rgba(255,255,255,0.01)') : 'transparent' }}>
                    <span className="font-body text-xs font-bold px-2 py-0.5 rounded-full inline-block" style={{ backgroundColor: plan.accent + '15', color: plan.priceOnRequest ? plan.accent : valueColor }}>
                      {plan.limits[row.field]}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
            {CMP_ROWS.map((row, i) => (
              <tr key={row.labelKey} style={{ borderTop: `1px solid ${rowBorder}` }}>
                <td className="p-3 pl-5 font-body text-xs" style={{ backgroundColor: labelBg, color: labelColor }}>{t(row.labelKey)}</td>
                {PLANS.map((plan) => (
                  <td key={plan.id} className="p-3 text-center" style={{ borderLeft: `1px solid ${rowBorder}`, backgroundColor: i % 2 === 0 ? (isLight ? 'rgba(10,22,40,0.01)' : 'rgba(255,255,255,0.01)') : 'transparent' }}>
                    {renderCell(row.cells[plan.id] ?? 'dash', plan.accent)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
